"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { type ReactNode } from "react";

import { type RouterOutput } from "@trip/api";

import { TripadvisorRating } from "~/components/tripadvisor-rating";
import { H3 } from "~/components/typography";
import { useCurrencyFormatter } from "~/lib/use-currency";
import { Link } from "~/navigation";
import { type RouterOutputs } from "~/trpc/types";

import { Cards } from "./cards";

function TourCard({
  inventory,
}: {
  inventory: RouterOutputs["inventory"]["search"]["results"][0];
}) {
  const t = useTranslations("TourDetails");
  const { amount, perPerson } = inventory;
  const formatCurrency = useCurrencyFormatter();
  return (
    <Link href={`/tours/${inventory.slug}/${inventory.id}`}>
      {inventory.imageUrl ? (
        <Image
          alt=""
          className="aspect-4/5 mb-4 w-full rounded-lg object-cover"
          width={220}
          height={264}
          src={inventory.imageUrl}
          unoptimized
        />
      ) : null}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <div className="text-muted flex items-center gap-1 text-xs font-light leading-none">
            {inventory.tripadvisorRating ? (
              <TripadvisorRating
                size="small"
                value={inventory.tripadvisorRating / 10}
              />
            ) : null}
            {inventory.tripadvisorRating && inventory.duration ? (
              <div className="bg-muted mx-1 size-1 rounded-full" />
            ) : null}
            {inventory.duration ? <div>{inventory.duration}</div> : null}
          </div>
          <div className="text-muted text-sm font-light leading-tight">
            {inventory.locationName}
          </div>
        </div>
        <div className="mt-1 line-clamp-2 font-medium leading-tight">
          {inventory.title}
        </div>
        <div className="text-muted flex items-baseline gap-1 text-sm font-light leading-tight">
          <span className="text-base text-neutral-900">
            {t("fromAmount", {
              currencyAmount: formatCurrency(amount),
            })}
          </span>{" "}
          {perPerson ? t("perPerson") : t("perGroup")}
        </div>
      </div>
    </Link>
  );
}

export function TourCards({
  title,
  tours,
}: {
  title: ReactNode;
  tours: RouterOutput["inventory"]["search"]["results"];
}) {
  return (
    <Cards
      items={tours.map((inventory) => ({
        inventory,
        id: inventory.id,
      }))}
      title={<H3>{title}</H3>}
      CardComponent={TourCard}
    />
  );
}
